import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useEffect, useState, useMemo } from "react";
import { JUST_LOGGED_OUT_KEY, loginRequest } from "../../../../AuthConfig";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";
import { useProfileRepository } from "../../../../data/repositories/ProfileRepository";
import { useCartContext } from "../../../../contexts/CartContext";
import { clearStoredImpersonation } from "../../../../contexts/OrganizationImpersonationContext";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { OrderState } from "../../../screens/order-list/vmHooks/useFunctionalListOrdersViewModel";
import { ApprovalStatus } from "../../../../core/models/entities/Approval";
import { enviroment } from "../../../../enviroment";

export interface NavigationBarViewModel {
  login: () => void;
  logout: () => void;
  isLoggingOut: boolean;
  userRole?: Role;
  companyLogo?: string;
  pendingOrdersCount?: number;
}

export function useNavigationBarViewModel(): NavigationBarViewModel {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { profile } = useProfileContext();
  const { clearProfile } = useProfileRepository();
  const { clearItems } = useCartContext();
  const { orderRepository } = useRepositoriesContext();
  const [role, setRole] = useState<Role>();
  const [companyLogo] = useState<string>();

  // Use React Query to fetch orders. Behind the gateOrdersBadge flag, gate the fetch to Vendors —
  // the only role that sees the pending-orders badge (NavigationBar renders it for userRole === Vendor).
  // Admins/customers skip this heavy pageSize=1000 fetch with no visible change. Flag off = always fetch.
  const ordersEnabled = enviroment.gateOrdersBadge
    ? isAuthenticated &&
      profile?.userOrganizationInformations?.role === Role.Vendor
    : true;
  // Server-side COUNT (organizations/orders/pending-count) replaces the old
  // pageSize=1000 fetch + client-side filter; the predicate is mirrored by
  // the API (Status Pending + vendor approval pending).
  const { data: pendingCountData } = orderRepository.usePendingOrdersCount(
    ordersEnabled
  );

  const pendingOrdersCount = useMemo(() => {
    if (
      isAuthenticated &&
      profile &&
      profile.userOrganizationInformations.role >= Role.Vendor &&
      pendingCountData
    ) {
      return pendingCountData.count;
    }
    return undefined;
  }, [isAuthenticated, profile, pendingCountData]);

  useEffect(() => {
    if (isAuthenticated && profile) {
      setRole(profile.userOrganizationInformations.role);
    }
  }, [isAuthenticated, profile]);

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  function login() {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest);
    }
  }

  function logout() {
    if (isAuthenticated && inProgress === InteractionStatus.None) {
      // Overlay first: the profile/cart clears below repaint the UI visibly
      // before the browser navigates to B2C - the overlay hides that flash.
      setIsLoggingOut(true);
      // Mark the logout as deliberate so UnauthenticatedView shows a
      // signed-out screen after the B2C round-trip instead of auto-starting a
      // new login. localStorage, NOT sessionStorage: Safari drops
      // sessionStorage across the cross-site logout round-trip, which lost the
      // marker and let the surviving B2C SSO cookie silently sign the user
      // back in (observed 07/07-2026: fix worked in Chrome, not Safari).
      // Værdien er et tidsstempel: en FRISK markør (sekunder gammel) betyder
      // "logout er i luften lige nu" og fryser både auto-login og
      // stale-rydningen i UnauthenticatedView - målt 14/8: markøren blev
      // sat ved +0ms og ryddet igen ved +32ms af netop den gren, hvorefter
      // auto-login vandt kapløbet mod logout-navigationen (landede på
      // /authorize, ikke /logout).
      localStorage.setItem(JUST_LOGGED_OUT_KEY, Date.now().toString());
      // Pass the account so MSAL sends id_token_hint to B2C's end_session -
      // without it B2C may keep its SSO session alive after "logout".
      instance.logoutRedirect({ account: accounts[0] ?? null });
      clearProfile();
      clearItems();
      // Act-as-vendor state must never survive into the next login on this tab
      // (Chrome keeps sessionStorage across the B2C round-trip; Safari doesn't).
      clearStoredImpersonation();
      // Flag REACT_APP_ROLE_ROUTE_REDIRECT_ENABLED (27/9): the post-login
      // deep-link marker (set by auto-login / AuthorizedFetch before a B2C
      // round-trip, restored role-blind on the next '/' landing) only has
      // meaning for the identity that set it. Without this, an admin tab's
      // marker (e.g. /manufacturer?...) survives logout in Chrome and sends
      // the NEXT login on this tab - a customer or vendor - onto an admin
      // route ("Ikke fundet"). Flag off = untouched, as today.
      if (enviroment.roleRouteRedirectEnabled) {
        sessionStorage.removeItem("redirectOnLogin");
      }
    }
  }

  return {
    login,
    logout,
    isLoggingOut,
    userRole: role,
    companyLogo,
    pendingOrdersCount,
    // customer
  };
}
